import { Trans, useLingui } from '@lingui/react/macro'

import { usePermissions } from '$src/lib/hooks/usePermissions'
import { usePropertyCreation } from '$src/lib/hooks/usePropertyCreation'
import { billingHooks, customerHooks } from '$src/lib/trpcHooks'
import {
  Badge,
  Button,
  Card,
  CloseButton,
  Collapse,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { CustomerWithRelations } from '@markku/types'
import { AddPropertyModal } from './AddPropertyModal'
import {
  IconBuildingCommunity,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCreditCard,
  IconSettings,
  IconPlus,
  IconUsers,
} from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

interface CustomerCardProps {
  customer: CustomerWithRelations
}

export function CustomerCard({ customer }: CustomerCardProps) {
  const { t } = useLingui()
  const [membersOpened, { toggle: toggleMembers }] = useDisclosure(false)
  // Members with app access. Excludes CRM-only contacts (accessRole=null)
  // such as imported isännöitsijät — they belong on a contact list, not a
  // resident-facing users list.
  const accessMembers =
    customer.members?.filter((m) => m.accessRole != null) ?? []
  const [
    propertyModalOpened,
    { open: openPropertyModal, close: closePropertyModal },
  ] = useDisclosure(false)
  const { handlePropertyCreated, WelcomeModal } = usePropertyCreation()
  const { mutate: getBillingPortalUrl, isPending: isBillingPending } =
    billingHooks.useGetBillingPortalUrl()
  const { isOwnerOf } = usePermissions()

  const handlePropertyCreatedAndCloseModal = (
    propertyId: number,
    propertyAddress: string,
  ) => {
    closePropertyModal()
    handlePropertyCreated(propertyId, propertyAddress)
  }

  const isOwner = isOwnerOf(customer.id)
  const hasStripeCustomer = !!customer.stripeCustomerId
  const canAccessBilling = isOwner && hasStripeCustomer

  const handleBillingPortal = () => {
    getBillingPortalUrl({ customerId: customer.id })
  }

  const handleEditSettings = () => {
    modals.open({
      title: t`Customer settings`,
      centered: true,
      children: (
        <EditCustomerSettingsForm
          customer={customer}
          onSuccess={() => modals.closeAll()}
        />
      ),
    })
  }

  return (
    <Card radius="md" withBorder>
      <Stack gap="md">
        {/* Customer Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" style={{ flex: 1 }}>
            <IconBuildingCommunity size={20} />
            <Text fw={600}>{customer.name}</Text>
            {isOwner && (
              <Button
                variant="light"
                size="compact-xs"
                onClick={handleEditSettings}
                p={4}
              >
                <IconSettings size={16} />
              </Button>
            )}
          </Group>
          <Badge
            variant="light"
            color={isOwner ? 'brandOrange' : 'gray'}
            style={{ flexShrink: 0 }}
          >
            {isOwner ? <Trans>Owner</Trans> : <Trans>Member</Trans>}
          </Badge>
        </Group>

        <Divider />

        {/* Properties Section */}
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" fw={500} c="dimmed">
              <Trans>Properties</Trans>
            </Text>
            {isOwner && (
              <Button
                variant="light"
                size="compact-sm"
                onClick={openPropertyModal}
                leftSection={<IconPlus size={16} />}
              >
                <Trans>Add property</Trans>
              </Button>
            )}
          </Group>

          {customer.properties?.length ? (
            <Stack gap="xs">
              {customer.properties.map((property) => {
                const activeSubscription = property.subscriptions?.find(
                  (sub) => sub.status === 'active',
                )

                return (
                  <Card
                    key={property.id}
                    component={Link}
                    to={`/properties/${property.id}`}
                    withBorder
                    radius="sm"
                    p="sm"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    styles={{
                      root: {
                        '&:hover': {
                          transform: 'translateX(4px)',
                          borderColor: 'var(--mantine-color-brandOrange-3)',
                        },
                      },
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Text size="sm" fw={500}>
                          {property.address?.street}, {property.address?.city}
                        </Text>
                        {activeSubscription ? (
                          <Group gap={4}>
                            <Badge size="xs" variant="light" color="green">
                              {activeSubscription.displayName}
                            </Badge>
                            <Group gap={2}>
                              {activeSubscription.effectivePriceMonthly &&
                                activeSubscription.effectivePriceMonthly !==
                                  activeSubscription.basePriceMonthly && (
                                  <Text size="xs" c="dimmed" td="line-through">
                                    {activeSubscription.basePriceMonthly}€
                                  </Text>
                                )}
                              <Text size="xs" c="dimmed">
                                {activeSubscription.effectivePriceMonthly ||
                                  activeSubscription.basePriceMonthly}
                                <Trans>€/mo</Trans>
                              </Text>
                            </Group>
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            <Trans>No active subscription</Trans>
                          </Text>
                        )}
                      </Stack>
                      <IconChevronRight
                        size={16}
                        color="var(--mantine-color-gray-5)"
                      />
                    </Group>
                  </Card>
                )
              })}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center">
              <Trans>No properties</Trans>
            </Text>
          )}
        </Stack>

        {/* Members Section — only show members with app access. CRM-only
            contacts (e.g. an imported isännöitsijä with accessRole=null and
            relationshipType='property_manager') belong to the back-office
            contact list, not the resident-facing users list. */}
        {accessMembers.length > 0 && (
          <>
            <Divider />
            <Stack gap="xs">
              <Group
                justify="space-between"
                onClick={toggleMembers}
                style={{ cursor: 'pointer' }}
              >
                <Group gap="xs">
                  <IconUsers size={16} color="var(--mantine-color-gray-6)" />
                  <Text size="sm" fw={500} c="dimmed">
                    <Trans>Members</Trans> ({accessMembers.length})
                  </Text>
                </Group>
                {membersOpened ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </Group>

              <Collapse in={membersOpened}>
                <Stack gap="xs" mt="xs">
                  {accessMembers.map((member) => (
                    <Card key={member.id} withBorder radius="sm" p="xs">
                      <Group justify="space-between">
                        <Stack gap={0}>
                          <Text size="sm" fw={500}>
                            {member.user?.firstName} {member.user?.lastName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {member.user?.email}
                          </Text>
                        </Stack>
                        <Badge
                          size="xs"
                          variant="light"
                          color={
                            member.accessRole === 'owner'
                              ? 'brandOrange'
                              : 'gray'
                          }
                        >
                          {member.accessRole === 'owner' ? (
                            <Trans>Owner</Trans>
                          ) : (
                            <Trans>Member</Trans>
                          )}
                        </Badge>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          </>
        )}

        {/* Billing Section */}
        {canAccessBilling && (
          <>
            <Divider />
            <Button
              leftSection={<IconCreditCard size={16} />}
              variant="light"
              onClick={handleBillingPortal}
              loading={isBillingPending}
            >
              <Trans>Manage payment method and invoices</Trans>
            </Button>
          </>
        )}
      </Stack>

      {/* Add Property Modal */}
      <AddPropertyModal
        opened={propertyModalOpened}
        onClose={closePropertyModal}
        customerId={customer.id}
        onPropertyCreated={handlePropertyCreatedAndCloseModal}
      />

      {WelcomeModal}
    </Card>
  )
}

export function EditCustomerSettingsForm({
  customer,
  onSuccess,
}: {
  customer: CustomerWithRelations
  onSuccess?: () => void
}) {
  const { t } = useLingui()
  const [name, setName] = useState(customer.name || '')
  const [billingEmail, setBillingEmail] = useState(customer.billingEmail ?? '')
  const { mutateAsync: updateCustomer, isPending } = customerHooks.useUpdate()

  const handleSubmit = async () => {
    if (!name.trim()) return

    const data: Record<string, unknown> = {}
    if (name.trim() !== (customer.name || '')) data.name = name.trim()
    if ((billingEmail.trim() || null) !== (customer.billingEmail ?? null))
      data.billingEmail = billingEmail.trim() || null

    if (Object.keys(data).length) {
      await updateCustomer({ id: customer.id, data })
    }
    onSuccess?.()
  }

  const ownerEmail = customer.members?.find((m) => m.accessRole === 'owner')
    ?.user?.email
  const effectiveEmail = billingEmail.trim() || ownerEmail

  return (
    <Stack gap="md">
      <TextInput
        label={t`Customer name`}
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />
      <TextInput
        label={t`Billing email`}
        description={t`Empty = owner email is used`}
        value={billingEmail}
        onChange={(e) => setBillingEmail(e.currentTarget.value)}
        type="email"
        rightSection={
          billingEmail ? (
            <CloseButton size="sm" onClick={() => setBillingEmail('')} />
          ) : null
        }
      />
      <Text size="sm" c="dimmed">
        <Trans>Invoices are sent to</Trans>{' '}
        <Text span fw={500} c="var(--mantine-color-text)">
          {effectiveEmail || '–'}
        </Text>
      </Text>
      <Button onClick={handleSubmit} loading={isPending}>
        <Trans>Save</Trans>
      </Button>
    </Stack>
  )
}
